import React from "react";
import styled from "styled-components";
import { Application, Batch, makeRequest } from "../../common/api";
import { isUserCreator } from "../../common/auth";
import { PageBody } from "../../common/components/PageBody";
import { LoadingIndicator, useTitle } from "../../common/features/Utils";
import { UserContext } from "../../common/UserContext";

declare var Plotly: any;

const Container = styled.div`
  padding: 20px;
`;

const InfoText = styled.p`
  margin-bottom: 24px;
  color: #666;
`;

const ControlsContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  overflow: visible;
`;

const ChartContainer = styled.div`
  margin-bottom: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background: white;
`;

const ChartTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 1.5em;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
  align-items: flex-start;
  
  .label-text {
    white-space: nowrap;
    margin-bottom: 4px;
    font-weight: 500;
    align-self: flex-start;
  }
  
  select {
    padding: 8px;
    min-width: 200px;
    width: 100%;
    border-radius: 4px;
    border: 1px solid #ccc;
    font-size: 14px;
  }
`;


// Helper function to get filtered batches based on search term
const getFilteredBatches = (batches: Batch[], searchTerm: string) => {
  return batches?.filter(batch => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const batchName = batch.name.toLowerCase();
    const batchStatus = batch.status.toLowerCase();
    const createdDate = new Date(batch.createdAt || '').toLocaleDateString().toLowerCase();
    
    return batchName.includes(searchLower) || 
           batchStatus.includes(searchLower) ||
           createdDate.includes(searchLower);
  }).sort((a, b) => {
    const dateA = new Date(a.createdAt || '');
    const dateB = new Date(b.createdAt || '');
    return dateB.getTime() - dateA.getTime(); // Newest first
  }) || [];
};

// Helper function to get chart colors
const getChartColors = () => [
  '#b181ea', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', 
  '#feca57', '#ff9ff3', '#54a0ff', '#5f27cd', '#00d2d3',
  '#ff6348', '#2ed573', '#ffa502', '#3742fa', '#2f3542'
];

// Helper function to calculate smart y-axis tick interval
const getSmartTickInterval = (maxValue: number) => {
  if (maxValue <= 5) return 1;
  if (maxValue <= 20) return 2;
  if (maxValue <= 50) return 5;
  return Math.ceil(maxValue / 10);
};

interface Props {}

export const ReportsPage = () => {
  useTitle("Reports");
  const userContext = React.useContext(UserContext);
  const user = userContext?.user;

  const [applications, setApplications] = React.useState<Application[] | null>(null);
  const [batches, setBatches] = React.useState<Batch[] | null>(null);
  const [selectedBatchIds, setSelectedBatchIds] = React.useState<string[]>([]);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [selectedTimeframe, setSelectedTimeframe] = React.useState<string>("3months");
  const [startDate, setStartDate] = React.useState<string>("");
  const [endDate, setEndDate] = React.useState<string>("");
  const [trendsViewMode, setTrendsViewMode] = React.useState<string>("monthly");
  const [loading, setLoading] = React.useState(true);
  const [plotlyLoaded, setPlotlyLoaded] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);


  // Load Plotly library
  React.useEffect(() => {
    const existingScript = document.getElementById("plotly-script");
    if (!existingScript) {
      const scriptTag = document.createElement("script");
      scriptTag.id = "plotly-script";
      scriptTag.src = "/lib/plotly.js";
      scriptTag.type = "text/javascript";
      scriptTag.onload = () => {
        setPlotlyLoaded(true);
      };
      document.body.appendChild(scriptTag);
    } else {
      setPlotlyLoaded(true);
    }
  }, []);

  // Fetch batches and initialize with active ones
  React.useEffect(() => {
    if (isUserCreator(user)) {
      makeRequest("/api/batches", "GET").then((data: Batch[]) => {
        setBatches(data);
        
        // Auto-select active batches on initial load
        const activeBatches = data.filter(batch => batch.status === "Open");
        const activeBatchIds = activeBatches.map(b => b._id);
        setSelectedBatchIds(activeBatchIds);
      }).catch(error => {
        console.error("Error fetching batches:", error);
        setLoading(false);
      });
    }
  }, [user]);

  // Fetch applications for selected batches
  React.useEffect(() => {
    if (batches && selectedBatchIds.length > 0) {
      const requestUrl = `/api/applications?batchIds=${selectedBatchIds.join(",")}`;
      
      makeRequest(requestUrl, "GET").then((data: Application[]) => {
        setApplications(data || []);
        setLoading(false);
      }).catch(error => {
        console.error("Error fetching applications:", error);
        setLoading(false);
      });
    } else if (batches && selectedBatchIds.length === 0) {
      setApplications([]);
      setLoading(false);
    }
  }, [batches, selectedBatchIds]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown-container]')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);



  // Initialize and update charts
  React.useEffect(() => {
    if (applications !== null && batches && !loading && plotlyLoaded) {
      renderEnrollmentTrendsChart();
      renderRelativeEnrollmentChart();
      renderActiveCoursesChart();
    }
  }, [applications, batches, selectedBatchIds, selectedTimeframe, startDate, endDate, trendsViewMode, loading, plotlyLoaded]);

  const getFilteredApplications = () => {
    if (!applications) return [];
    
    let filtered = applications;
    
    // Filter by timeframe
    if (selectedTimeframe === "custom" && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end) {
        end.setHours(23, 59, 59, 999);
        
        filtered = filtered.filter(app => {
          const appDate = new Date(app.createdAt);
          return appDate >= start && appDate <= end;
        });
      }
    } else if (selectedTimeframe !== "all") {
      const now = new Date();
      const cutoffDate = new Date();
      
      switch (selectedTimeframe) {
        case "1month":
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case "3months":
          cutoffDate.setMonth(now.getMonth() - 3);
          break;
        case "6months":
          cutoffDate.setMonth(now.getMonth() - 6);
          break;
        case "1year":
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      filtered = filtered.filter(app => new Date(app.createdAt) >= cutoffDate);
    }
    
    return filtered;
  };


  const getWeekKey = (date: Date) => {
    const year = date.getFullYear();
    const week = getWeekNumber(date);
    return `${year}-W${week.toString().padStart(2, '0')}`;
  };

  const getWeekNumber = (date: Date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  const renderEnrollmentTrendsChart = () => {
    if (typeof Plotly === 'undefined') return;
    
    const filteredApps = getFilteredApplications();
    const selectedBatches = batches?.filter(batch => selectedBatchIds.includes(batch._id)) || [];
    
    // If no batches selected, show empty state
    if (selectedBatches.length === 0) {
      const data = [{
        x: ['No Data'],
        y: [0],
        type: 'scatter',
        mode: 'markers',
        marker: { color: '#ccc', size: 10 },
        name: 'No Data'
      }];
      
      Plotly.react("enrollmentTrendsChart", data, {
        title: `Enrollment Trends Over Time (${trendsViewMode === 'weekly' ? 'Weekly' : 'Monthly'})`,
        height: 400,
        xaxis: {
          title: trendsViewMode === 'weekly' ? "Week" : "Month"
        },
        yaxis: {
          title: "Number of Applications",
          tickformat: 'd',
          rangemode: 'tozero'
        },
        showlegend: false,
        annotations: [{
          x: 'No Data',
          y: 0,
          text: 'Please select courses to view trends',
          showarrow: false,
          font: { size: 14, color: '#666' }
        }]
      });
      return;
    }

    const colors = getChartColors();

    // Group applications by batch and time period
    const batchTimeData: { [key: string]: { name: string; color: string; data: { [key: string]: number } } } = {};
    
    selectedBatches.forEach((batch, index) => {
      batchTimeData[batch._id] = {
        name: batch.name,
        color: colors[index % colors.length],
        data: {}
      };
    });

    // Process applications
    filteredApps.forEach(app => {
      const date = new Date(app.createdAt);
      if (isNaN(date.getTime())) {
        console.warn('Invalid date for application:', app._id, app.createdAt);
        return;
      }
      
      let timeKey;
      if (trendsViewMode === 'weekly') {
        timeKey = getWeekKey(date);
      } else {
        // Monthly view
        timeKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      }
      
      if (batchTimeData[app.batchId]) {
        batchTimeData[app.batchId].data[timeKey] = (batchTimeData[app.batchId].data[timeKey] || 0) + 1;
      }
    });

    // Get all time periods across all batches
    const allTimePeriods = new Set();
    Object.values(batchTimeData).forEach(batch => {
      Object.keys(batch.data).forEach(period => allTimePeriods.add(period));
    });
    const sortedPeriods = Array.from(allTimePeriods).sort();

    // If no data, show empty state
    if (sortedPeriods.length === 0) {
      const data = [{
        x: ['No Data'],
        y: [0],
        type: 'scatter',
        mode: 'markers',
        marker: { color: '#ccc', size: 10 },
        name: 'No Data'
      }];
      
      Plotly.react("enrollmentTrendsChart", data, {
        title: `Enrollment Trends Over Time (${trendsViewMode === 'weekly' ? 'Weekly' : 'Monthly'})`,
        height: 400,
        xaxis: {
          title: trendsViewMode === 'weekly' ? "Week" : "Month"
        },
        yaxis: {
          title: "Number of Applications",
          tickformat: 'd',
          rangemode: 'tozero'
        },
        showlegend: false,
        annotations: [{
          x: 'No Data',
          y: 0,
          text: 'No applications found for selected time period',
          showarrow: false,
          font: { size: 14, color: '#666' }
        }]
      });
      return;
    }

    // Create data traces for each batch
    const data = Object.values(batchTimeData).map((batch: { name: string; color: string; data: { [key: string]: number } }) => {
      const yValues = sortedPeriods.map((period: string) => batch.data[period] || 0);
      
      return {
        x: sortedPeriods,
        y: yValues,
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: batch.color, width: 3 },
        marker: { color: batch.color, size: 6 },
        name: batch.name,
        connectgaps: false
      };
    });

    // Calculate smart y-axis settings
    const allValues = data.flatMap(trace => trace.y);
    const maxValue = Math.max(...allValues, 1);
    const dtick = getSmartTickInterval(maxValue);

    Plotly.react("enrollmentTrendsChart", data, {
      title: `Enrollment Trends by Course (${trendsViewMode === 'weekly' ? 'Weekly' : 'Monthly'})`,
      height: 400,
      xaxis: {
        title: trendsViewMode === 'weekly' ? "Week" : "Month",
        type: 'category',
        tickangle: trendsViewMode === 'weekly' ? -45 : 0
      },
      yaxis: {
        title: "Number of Applications",
        tickformat: 'd',
        dtick: dtick,
        rangemode: 'tozero',
        automargin: true
      },
      showlegend: selectedBatches.length > 1,
      legend: {
        x: 1,
        y: 1,
        xanchor: 'left',
        bgcolor: 'rgba(255,255,255,0.8)',
        bordercolor: '#ccc',
        borderwidth: 1
      },
      margin: {
        l: 60,
        r: selectedBatches.length > 1 ? 150 : 20,
        t: 60,
        b: trendsViewMode === 'weekly' ? 80 : 50
      }
    });
  };

  const renderRelativeEnrollmentChart = () => {
    if (typeof Plotly === 'undefined') return;
    
    const filteredApps = getFilteredApplications();
    const selectedBatches = batches?.filter(batch => selectedBatchIds.includes(batch._id)) || [];
    
    if (selectedBatches.length === 0) {
      const data = [{
        x: ['No Data'],
        y: [0],
        type: 'scatter',
        mode: 'markers',
        marker: { color: '#ccc', size: 10 },
        name: 'No Data'
      }];
      
      Plotly.react("relativeEnrollmentChart", data, {
        title: "Enrollment Trends Relative to Batch Opening",
        height: 400,
        xaxis: { title: "Week from Opening" },
        yaxis: { title: "Number of Applications", tickformat: 'd', rangemode: 'tozero' },
        showlegend: false,
        annotations: [{
          x: 'No Data',
          y: 0,
          text: 'Please select courses to view relative trends',
          showarrow: false,
          font: { size: 14, color: '#666' }
        }]
      });
      return;
    }

    const colors = getChartColors();

    // Process each batch to get relative week data
    const batchRelativeData: { [key: string]: { name: string; color: string; data: { [key: number]: number } } } = {};
    
    selectedBatches.forEach((batch, index) => {
      batchRelativeData[batch._id] = {
        name: batch.name,
        color: colors[index % colors.length],
        data: {}
      };
      
      // Get batch opening date (use createdAt as opening date)
      const batchOpeningDate = new Date(batch.createdAt || '');
      if (isNaN(batchOpeningDate.getTime())) {
        console.warn('Invalid opening date for batch:', batch._id, batch.createdAt);
        return;
      }
      
      // Process applications for this batch
      const batchApps = filteredApps.filter(app => app.batchId === batch._id);
      
      batchApps.forEach(app => {
        const appDate = new Date(app.createdAt);
        if (isNaN(appDate.getTime())) return;
        
        // Calculate weeks from batch opening
        const timeDiff = appDate.getTime() - batchOpeningDate.getTime();
        const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const weekFromOpening = Math.floor(daysDiff / 7) + 1; // Week 1, 2, 3, etc.
        
        // Only count positive weeks (applications after opening)
        if (weekFromOpening > 0 && weekFromOpening <= 52) { // Max 52 weeks
          batchRelativeData[batch._id].data[weekFromOpening] = 
            (batchRelativeData[batch._id].data[weekFromOpening] || 0) + 1;
        }
      });
    });

    // Find the maximum week across all batches to create consistent x-axis
    const allWeeks = new Set<number>();
    Object.values(batchRelativeData).forEach(batch => {
      Object.keys(batch.data).forEach(week => allWeeks.add(parseInt(week)));
    });
    
    const sortedWeeks = Array.from(allWeeks).sort((a, b) => a - b);

    if (sortedWeeks.length === 0) {
      const data = [{
        x: ['No Data'],
        y: [0],
        type: 'scatter',
        mode: 'markers',
        marker: { color: '#ccc', size: 10 }
      }];
      
      Plotly.react("relativeEnrollmentChart", data, {
        title: "Enrollment Trends Relative to Batch Opening",
        height: 400,
        annotations: [{
          x: 'No Data',
          y: 0,
          text: 'No applications found after batch opening dates',
          showarrow: false,
          font: { size: 14, color: '#666' }
        }]
      });
      return;
    }

    // Create data traces for each batch
    const data = Object.values(batchRelativeData).map(batch => {
      const yValues = sortedWeeks.map(week => batch.data[week] || 0);
      
      return {
        x: sortedWeeks.map(week => `Week ${week}`),
        y: yValues,
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: batch.color, width: 3 },
        marker: { color: batch.color, size: 6 },
        name: batch.name,
        connectgaps: false
      };
    });

    // Calculate smart y-axis settings
    const allValues = data.flatMap(trace => trace.y);
    const maxValue = Math.max(...allValues, 1);
    const dtick = getSmartTickInterval(maxValue);

    Plotly.react("relativeEnrollmentChart", data, {
      title: "Enrollment Trends Relative to Course Opening",
      height: 400,
      xaxis: {
        title: "Weeks from Course Opening",
        type: 'category',
        tickangle: sortedWeeks.length > 10 ? -45 : 0
      },
      yaxis: {
        title: "Number of Applications",
        tickformat: 'd',
        dtick: dtick,
        rangemode: 'tozero',
        automargin: true
      },
      showlegend: selectedBatches.length > 1,
      legend: {
        x: 1,
        y: 1,
        xanchor: 'left',
        bgcolor: 'rgba(255,255,255,0.8)',
        bordercolor: '#ccc',
        borderwidth: 1
      },
      margin: {
        l: 60,
        r: selectedBatches.length > 1 ? 150 : 20,
        t: 60,
        b: sortedWeeks.length > 10 ? 80 : 50
      }
    });
  };

  const renderActiveCoursesChart = () => {
    if (typeof Plotly === 'undefined' || !batches) return;

    const selectedBatches = batches.filter(batch => selectedBatchIds.includes(batch._id));
    const batchData = selectedBatches.map(batch => {
      const batchApps = applications?.filter(app => app.batchId === batch._id) || [];
      return {
        name: batch.name,
        count: batchApps.length,
        status: batch.status
      };
    });

    // Sort by count descending
    batchData.sort((a, b) => b.count - a.count);

    // Calculate smart y-axis settings
    const values = batchData.map(d => d.count);
    const maxValue = Math.max(...values, 1);
    const dtick = getSmartTickInterval(maxValue);

    const data = [{
      x: batchData.map(d => d.name),
      y: values,
      type: 'bar',
      marker: { color: '#b181ea' },
      text: batchData.map(d => `${d.count} apps`),
      textposition: 'outside'
    }];

    Plotly.react("activeCoursesChart", data, {
      title: `Applications per Selected Course (${selectedBatches.length} courses)`,
      height: 400,
      xaxis: {
        title: "Course",
        tickangle: batchData.length > 5 ? -45 : 0
      },
      yaxis: {
        title: "Number of Applications",
        tickformat: 'd',
        dtick: dtick,
        rangemode: 'tozero',
        automargin: true
      },
      showlegend: false,
      margin: {
        l: 60,
        r: 20,
        t: 60,
        b: batchData.length > 5 ? 120 : 80
      }
    });
  };


  if (!isUserCreator(user)) {
    return (
      <PageBody>
        <Container>
          <div>
            Please log in to a valid creator account to access this page.
          </div>
        </Container>
      </PageBody>
    );
  }

  if (loading || !plotlyLoaded) {
    return (
      <PageBody>
        <Container>
          <LoadingIndicator />
          {!plotlyLoaded && <p>Loading charts...</p>}
        </Container>
      </PageBody>
    );
  }


  return (
    <PageBody>
      <Container>
        <InfoText>
          View enrollment analytics and historical data for your courses and applications.
        </InfoText>

        {selectedTimeframe === "custom" && (!startDate || !endDate) && (
          <InfoText style={{ color: "#ff6b6b", fontSize: "14px" }}>
            Please select both start and end dates for the custom date range.
          </InfoText>
        )}

        {selectedTimeframe === "custom" && startDate && endDate && new Date(startDate) > new Date(endDate) && (
          <InfoText style={{ color: "#ff6b6b", fontSize: "14px" }}>
            Start date must be before or equal to end date.
          </InfoText>
        )}

        <ControlsContainer>
          <div style={{ width: "100%" }}>
            <Label>
              <span className="label-text">Select Courses:</span>
              
            </Label>
              {/* Interactive Search Area */}
              <div data-dropdown-container style={{ position: 'relative' }}>
                <div
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  style={{
                    minHeight: '48px',
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                >
                  {/* Selected Courses Chips */}
                  {selectedBatchIds.length > 0 ? (
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '6px', 
                      marginBottom: selectedBatchIds.length > 0 ? '8px' : '0'
                    }}>
                      {selectedBatchIds.map(batchId => {
                        const batch = batches?.find(b => b._id === batchId);
                        if (!batch) return null;
                        return (
                          <div key={batchId} style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px 8px',
                            backgroundColor: '#b181ea',
                            color: 'white',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            <span style={{ marginRight: '6px' }}>{batch.name}</span>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedBatchIds(selectedBatchIds.filter(id => id !== batchId));
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                padding: '0',
                                marginLeft: '2px',
                                fontSize: '14px',
                                lineHeight: '1'
                              }}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  
                  {/* Search Input */}
                  <input
                    type="text"
                    placeholder={selectedBatchIds.length === 0 ? "Search and select courses..." : "Search for more courses..."}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (!isDropdownOpen && e.target.value) {
                        setIsDropdownOpen(true);
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDropdownOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setIsDropdownOpen(false);
                        setSearchTerm('');
                      }
                    }}
                    style={{
                      width: '100%',
                      border: 'none',
                      outline: 'none',
                      fontSize: '14px',
                      backgroundColor: 'transparent',
                      color: '#333'
                    }}
                  />
                </div>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    minWidth: '400px',
                    width: 'max-content',
                    maxWidth: '600px',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}>
                    {/* Quick Actions in Dropdown */}
                    <div style={{ 
                      padding: '12px', 
                      borderBottom: '1px solid #eee',
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}>
                      <button
                        onClick={() => {
                          const filteredBatches = getFilteredBatches(batches || [], searchTerm)
                            .filter(batch => batch.status === 'Open')
                            .map(b => b._id);
                          setSelectedBatchIds(filteredBatches);
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #22c55e',
                          borderRadius: '4px',
                          background: 'white',
                          color: '#22c55e',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Active Only ({getFilteredBatches(batches || [], searchTerm).filter(b => b.status === 'Open').length})
                      </button>
                      <button
                        onClick={() => {
                          const filteredBatches = getFilteredBatches(batches || [], searchTerm).map(b => b._id);
                          setSelectedBatchIds(filteredBatches);
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #666',
                          borderRadius: '4px',
                          background: 'white',
                          color: '#666',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Select All ({getFilteredBatches(batches || [], searchTerm).length})
                      </button>
                      <button
                        onClick={() => setSelectedBatchIds([])}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ff6b6b',
                          borderRadius: '4px',
                          background: 'white',
                          color: '#ff6b6b',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          const fullStackBatches = getFilteredBatches(batches || [], searchTerm)
                            .filter(batch => batch.name.toLowerCase().includes('full stack'))
                            .map(b => b._id);
                          setSelectedBatchIds(fullStackBatches);
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #3b82f6',
                          borderRadius: '4px',
                          background: 'white',
                          color: '#3b82f6',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        All Full Stack ({getFilteredBatches(batches || [], searchTerm).filter(batch => batch.name.toLowerCase().includes('full stack')).length})
                      </button>
                      <button
                        onClick={() => {
                          const javaBatches = getFilteredBatches(batches || [], searchTerm)
                            .filter(batch => batch.name.toLowerCase().includes('java'))
                            .map(b => b._id);
                          setSelectedBatchIds(javaBatches);
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #f59e0b',
                          borderRadius: '4px',
                          background: 'white',
                          color: '#f59e0b',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        All Java ({getFilteredBatches(batches || [], searchTerm).filter(batch => batch.name.toLowerCase().includes('java')).length})
                      </button>
                    </div>

                    {/* Course Options */}
                    {(() => {
                      const filteredBatches = getFilteredBatches(batches || [], searchTerm);

                      if (filteredBatches.length === 0 && searchTerm) {
                        return (
                          <div style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#666',
                            fontSize: '14px'
                          }}>
                            No courses found matching "{searchTerm}"
                          </div>
                        );
                      }

                      return filteredBatches.map(batch => (
                      <label key={batch._id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: selectedBatchIds.includes(batch._id) ? '#f8f6ff' : 'transparent',
                        minHeight: '50px',
                        width: '100%'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = selectedBatchIds.includes(batch._id) ? '#f8f6ff' : '#f9f9f9';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = selectedBatchIds.includes(batch._id) ? '#f8f6ff' : 'transparent';
                      }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedBatchIds.includes(batch._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBatchIds([...selectedBatchIds, batch._id]);
                            } else {
                              setSelectedBatchIds(selectedBatchIds.filter(id => id !== batch._id));
                            }
                          }}
                          style={{ 
                            marginRight: '12px',
                            width: '16px',
                            height: '16px',
                            accentColor: '#b181ea'
                          }}
                        />
                        <div style={{ 
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%'
                        }}>
                          <div>
                            <div style={{ 
                              fontWeight: selectedBatchIds.includes(batch._id) ? '600' : '500',
                              marginBottom: '4px',
                              fontSize: '15px'
                            }}>
                              {batch.name}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#666'
                            }}>
                              Created: {new Date(batch.createdAt || '').toLocaleDateString() || 'N/A'}
                            </div>
                          </div>
                          <div style={{
                            padding: '6px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: batch.status === 'Open' ? '#dcfce7' : '#f3f4f6',
                            color: batch.status === 'Open' ? '#166534' : '#374151'
                          }}>
                            {batch.status}
                          </div>
                        </div>
                      </label>
                      ));
                    })()}
                  </div>
                )}
              </div>
          </div>

          <Label>
            <span className="label-text">Time Period:</span>
            <select
              value={selectedTimeframe}
              onChange={(e) => {
                const newTimeframe = e.target.value;
                setSelectedTimeframe(newTimeframe);
                
                if (newTimeframe === "custom") {
                  // Set default dates to last 6 months when switching to custom
                  const end = new Date();
                  const start = new Date();
                  start.setMonth(end.getMonth() - 6);
                  
                  setEndDate(end.toISOString().split('T')[0]);
                  setStartDate(start.toISOString().split('T')[0]);
                } else {
                  // Clear custom dates when switching away from custom
                  setStartDate("");
                  setEndDate("");
                }
              }}
            >
              <option value="1month">Last Month</option>
              <option value="3months">Last 3 Months</option>
              <option value="6months">Last 6 Months</option>
              <option value="1year">Last Year</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Date Range</option>
            </select>
          </Label>

          {selectedTimeframe === "custom" && (
            <>
              <Label>
                <span className="label-text">Start Date:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    padding: "8px",
                    minWidth: "200px",
                    width: "100%",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    fontSize: "14px"
                  }}
                />
              </Label>

              <Label>
                <span className="label-text">End Date:</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    padding: "8px",
                    minWidth: "200px",
                    width: "100%",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    fontSize: "14px"
                  }}
                />
              </Label>
            </>
          )}
        </ControlsContainer>


        <ChartContainer>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <ChartTitle style={{ margin: 0 }}>Enrollment Trends Over Time</ChartTitle>
            <Label style={{ minWidth: 'auto', margin: 0 }}>
              <span className="label-text">View:</span>
              <select
                value={trendsViewMode}
                onChange={(e) => setTrendsViewMode(e.target.value)}
                style={{
                  padding: "6px",
                  minWidth: "120px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "14px"
                }}
              >
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
              </select>
            </Label>
          </div>
          <div id="enrollmentTrendsChart" style={{ minHeight: "400px" }}></div>
        </ChartContainer>

        <ChartContainer>
          <ChartTitle>Enrollment Patterns Relative to Course Launch</ChartTitle>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
            Shows application patterns by weeks from when each course opened (Week 1, Week 2, etc.)
          </div>
          <div id="relativeEnrollmentChart" style={{ minHeight: "400px" }}></div>
        </ChartContainer>

        <ChartContainer>
          <ChartTitle>Course Enrollment Overview</ChartTitle>
          <div id="activeCoursesChart" style={{ minHeight: "400px" }}></div>
        </ChartContainer>
      </Container>
    </PageBody>
  );
};
